import { type FC, useMemo } from 'react';

import { NoSsr } from '@mui/material';
import { getFormattedRoundedPrice } from '@vakantiesnl/services/src/util/mathUtils';
import { FlashDeals } from '@vakantiesnl/types/src';

import { useCustomStyles } from './ZooverDealBar.style';
import { Countdown } from '../../atoms/Countdown';
import { Icon } from '../../atoms/Icon';
import { Tag } from '../../atoms/Tag';
import { Typography } from '../../atoms/Typography';

type Props = {
	title: string;
	expirationDate: Date;
	discountAmount?: number;
	discountType?: FlashDeals.DISCOUNT_TYPE;
	className?: string;
	variant: 'rounded' | 'priceCard' | 'squared';
};

export const ZooverDealBar: FC<Props> = ({
	title,
	expirationDate,
	discountAmount,
	discountType,
	className,
	variant,
}) => {
	const { classes, cx } = useCustomStyles();

	const discountTag = useMemo(() => {
		if (!discountAmount || !discountType || discountAmount <= 0) return null;

		const formattedValue =
			discountType === FlashDeals.DISCOUNT_TYPE.PERCENTAGE
				? `-${discountAmount}%`
				: `-${getFormattedRoundedPrice(discountAmount)}`;

		return (
			<Tag rounded={true} variant={'filled'} size={'small'} color={'whiteAlt'}>
				{formattedValue}
			</Tag>
		);
	}, [discountAmount, discountType]);

	return (
		<div className={cx(classes.barWrapper, classes[variant], className)}>
			<div className={classes.barTitle}>
				<Icon type="bolt" />
				<Typography variant="labelSm" as="span" className={classes.barTitleTitle}>
					{title}
				</Typography>
				{discountTag}
			</div>
			<div className={classes.countdown}>
				{/** No SSR due to the countdown that will be different on server and client */}
				<NoSsr>
					<Typography variant="labelSm" as="span">
						<Countdown expirationDate={expirationDate} />
					</Typography>
				</NoSsr>
			</div>
		</div>
	);
};
